const swea = require('swea');
import * as globals from '../globals';
const ViewerManager = swea.editor.ViewerManager;
const InteractionManager = swea.editor.InteractionManager;
const InstructionServiceActions = swea.react.actions.InstructionServiceActions;
const ImageViewerServiceActions = swea.react.actions.ImageViewerServiceActions;
const ContextMenuStateServiceActions = swea.react.actions.ContextMenuStateServiceActions;
const BrowserInfo = swea.react.helpers.BrowserInfo;
const DirectCameraAdapter = swea.editor.DirectCameraAdapter;
const CameraMatrixHelper = swea.scene.viewers.CameraMatrixHelper;

/**
 * A custom implementation of the best camera interaction.
 */
class CustomBestCamerasInteraction extends swea.editor.interactions.BestCamerasInteraction {
  /**
   * Constructs the interaction.
   * @param { object } options An option object, see {@link BestCamerasInteraction} for more info.
   * @param { boolean } options.imageSelectionInteraction The image selection interaction.
   * @param { boolean } options.sameFolder True to smart-navigate from same folder only. Default is false and no effect if not smart.
   * @param { boolean } options.reopen (optional) True to reopen image catalog after closing image view. Default is true.
   * @param { boolean } options.showDownload (optional) False to disable download image icon. Default is true.
   * @param { boolean } options.showOrthogonalGsd (optional) True to measure and show the orthogonal GSD, if any.
   * @param { string } options.orthogonalGsdTitle (optional) The orthogonal GSD title, default is "Orthogonal GSD".
   * @param { boolean } options.forceOverlayViewer (optional) True to force setting overlay viewer even if mobile, to update e.g. compass and altitude. Default is false.
   */
  constructor(options) {
    super(options);
    this._imageSelectionInteraction = options.imageSelectionInteraction;
    if (this._imageSelectionInteraction !== undefined) {
      this._imageSelectionInteraction.setReopen(!this._smart);
      this._imageSelectionInteraction.setInspectionPoint(undefined);
    }
    this._sameFolder = (options.sameFolder === undefined ? false : options.sameFolder);
    this._reopen = (options.reopen === undefined ? true : options.reopen);
    this._showDownload = (options.showDownload === undefined ? true : options.showDownload);
    this._showOrthogonalGsd = (options.showOrthogonalGsd === undefined ? false : options.showOrthogonalGsd);
    this._orthogonalGsdTitle = (options.orthogonalGsdTitle === undefined ? 'Orthogonal GSD' : options.orthogonalGsdTitle);
    this._forceOverlayViewer = (options.forceOverlayViewer === undefined ? false : options.forceOverlayViewer);
    this._disabledInteractionClasses = [];
  }

  /**
   * Initializes the interaction.
   * @param element
   * @param viewerId
   */
  initialize(element, viewerId) {
    super.initialize(element, viewerId);

    // Add a trigger that closes the viewer or cancels the interaction.
    this.addTrigger(new swea.editor.interactions.EventHandler(
      [swea.editor.interactions.eventCodes.ESC],
      this,
      () => {
        this.terminate();
      })
    );
  }

  /**
   * Overridden to set instruction depending on smart inspection.
   * @private
   * @override
   */
  _whenUpdated() {
    if (this._smart) {
      InstructionServiceActions.showInstruction('instructions.inspection_smart_interaction');
    }
    else {
      InstructionServiceActions.showInstruction('instructions.inspection_interaction');
    }

    if (this._imageSelectionInteraction !== undefined) {
      this._imageSelectionInteraction.setReopen(this._smart || !this._reopen ? false : true);
      this._imageSelectionInteraction.setInspectionPoint(undefined);
    }
  }

  /**
   * When the interaction is initialized set the cursor.
   * @param element { DOMElement } The element that the interaction works on
   * @param viewerId { number } The viewerId
   */
  whenInitialized(element, viewerId){
    super.whenInitialized(element, viewerId);
    this._disabledInteractionClasses = InteractionManager.instance().disableContinuousInteractions();
    ViewerManager.instance().getViewerById(viewerId).setCursor('crosshair');
    if (this._smart) {
      InstructionServiceActions.showInstruction('instructions.inspection_smart_interaction');
    }
    else {
      InstructionServiceActions.showInstruction('instructions.inspection_interaction');
    }
  }

  whenCameraFound(bestCamera) {
    if (!this._smart) {
      super.whenCameraFound(bestCamera);
      return;
    }
    if (bestCamera === undefined) {
      // Shouldn't happen
      return;
    }

    const overlayViewer = (BrowserInfo.isMobile() && !this._forceOverlayViewer ? undefined : ViewerManager.instance().getViewerById(this._viewerId));

    this.getSceneViewer().flyToCamera(bestCamera).then(() => {
      ImageViewerServiceActions.open({
        imageNames: [bestCamera.name],
        overlayViewer: overlayViewer,
        showDownload: this._showDownload,
        showOrthogonalGsd: this._showOrthogonalGsd,
        orthogonalGsdTitle: this._orthogonalGsdTitle,
        enableFavorites: false,
        smartNavigation: true,
        smartNavigationImages: this._smartInspectImages,
        sameFolder: this._sameFolder,
        arrowButtons: false
      });
      InstructionServiceActions.showInstruction('instructions.smart_image_viewer');
    });
  }

  /**
   * Open the image viewer when the images have been loaded.
   * Tihs hook is triggered by the user clicking in the model.
   * @param data
   */
  whenCamerasFound(data){
    if (this._smart) {
      super.whenCamerasFound(data);
      return;
    }
    
    if (data.cameras.length == 0 || this._latestHit === undefined) {
      return;
    }

    // TEMP
    /*
    DirectCameraAdapter._histogramDebugIndex = 0;
    for (let i = 0; i < data.cameras.length; ++i) {
      DirectCameraAdapter._calculateHistogram(swea.editor.InspectionAdapter.getBaseUrl() + '/images/thumbs/' + data.cameras[i].name);
    }
    */

    const point = this._latestHit.getPosition();
    // NOTE: For debugging specific coordinate
    // const point = [-11.468619757356057, 14.460301972663869, 38.81494257404905];

    if (this._ctrl) {
      // Direct best image (don't consider angle)
      const bestCamera = DirectCameraAdapter.findDirectCamera(point, data.cameras, true, true, false);
      if (bestCamera !== undefined) {
        const overlayViewer = (BrowserInfo.isMobile() && !this._forceOverlayViewer ? undefined : ViewerManager.instance().getViewerById(this._viewerId));
        ImageViewerServiceActions.open({
          imageNames: [bestCamera.name],
          overlayViewer: overlayViewer,
          showNumbers: false,
          showDownload: this._showDownload,
          showOrthogonalGsd: this._showOrthogonalGsd,
          orthogonalGsdTitle: this._orthogonalGsdTitle,
          enableFavorites: false,
          arrowButtons: true,
          chronological: true,
          highlightPoint: point
        });
        InstructionServiceActions.showInstruction('instructions.direct_image_viewer');
        
        this._imageSelectionInteraction.setReopen(false);
      }
    }
    else {
      // NOTE: Don't use this anymore since FindOptimalCamera in SpotscalePipe should do a better job nowadays
      /*
      // Create image catalog, ordered by "best photo"
      const orderedCameras = data.cameras.slice();
      orderedCameras.sort((c1, c2) => {
        return DirectCameraAdapter.calculateWeight(c1, point, true, true, false) - DirectCameraAdapter.calculateWeight(c2, point, true, true, false);
      });
      
      const imageCatalogue = {
        cameras: orderedCameras
      };
      */

      const filteredCameras = data.cameras.filter(cam => CameraMatrixHelper.isPointInCamera(cam, point));
      const imageCatalogue = {
        cameras: filteredCameras
      };
      
      this._imageSelectionInteraction.setInspectionPoint(point);
      this._imageSelectionInteraction.open(imageCatalogue);
    }
  }

  whenTerminated(element, viewerId){
    super.whenTerminated(element, viewerId);
    ViewerManager.instance().getViewerById(viewerId).setCursor('default');
    InteractionManager.instance().enableContinuousInteractions(this._disabledInteractionClasses);
    ContextMenuStateServiceActions.closeContextMenu(
      globals.LOWER_CONTEXT_MENU
    );
  }
  
  whenCamerasNotFound(){
    InstructionServiceActions.showInstruction('instructions.no_image_available');
    this._imageSelectionInteraction.setInspectionPoint(undefined);
  }
}
export default CustomBestCamerasInteraction;
